// M004-F0646 - "+ Add plan" inline picker (design §3.4; an in-drawer panel,
// never a dialog): search, room filter, "Zoned only", a mini week glyph per
// plan, "Add for all or selected rooms" for plans sharing a template, and an
// effective-from date. Choosing only STAGES a draft card; Save commits.
import React, { useMemo, useState } from 'react';
import {
  DAY_INITIALS, filterPickerPlans, formatPerHour, glyphCells, scopeSummary, styleForPlan,
} from './planModel';
import { ScopeChips } from './PlanCard';

const Glyph = ({ plan, style }) => {
  const rows = glyphCells(plan);
  return (
    <div className="mpp-glyph" style={{ '--mpp-c': style.color }} aria-hidden="true" data-testid="picker-glyph">
      {DAY_INITIALS.map((d, index) => <div className="mpp-glyph-head" key={`h${index}`}>{d}</div>)}
      {rows.map((row, r) => row.map((on, day) => (
        <div
          key={`${r}-${day}`}
          className="mpp-glyph-cell"
          data-on={on ? 'true' : 'false'}
          style={on ? { backgroundColor: style.color } : undefined}
        />
      )))}
    </div>
  );
};

const PickerRow = ({ row, styles, rooms, defaultFrom, onAdd }) => {
  const [mode, setMode] = useState('all');
  const [picked, setPicked] = useState(() => row.rooms.map((r) => String(r.roomId)));
  const [from, setFrom] = useState(defaultFrom);
  const style = styleForPlan(styles, row.plan.id);
  const multi = row.rooms.length > 1;
  const chosen = mode === 'all' ? row.rooms : row.rooms.filter((r) => picked.includes(String(r.roomId)));
  const scope = scopeSummary(row.plan);
  const roomLabels = row.rooms.map((r) => ({ key: String(r.planId), label: r.roomName }));
  return (
    <div className="mpp-pitem" data-testid="picker-plan" data-plan-key={row.key}>
      <Glyph plan={row.plan} style={style} />
      <div className="mpp-pbody">
        <div className="mpp-row mpp-between">
          <b>{row.name}</b>
          {Number.isFinite(row.firstHourMinor) ? <span className="mpp-rate">{formatPerHour(row.firstHourMinor)}</span> : null}
        </div>
        <ScopeChips roomLabels={multi ? [{ key: 'all', label: `${row.rooms.length} rooms` }] : roomLabels} plan={row.plan} style={style} />
        {scope.zoned ? <div className="mpp-muted">Zone: applies only inside {scope.times.join(', ')}.</div> : null}
        {multi ? (
          <div className="mpp-addfor" role="group" aria-label={`Add ${row.name} for`}>
            <span className="mpp-muted">Add for:</span>
            <label><input type="radio" name={`af-${row.key}`} checked={mode === 'all'} onChange={() => setMode('all')} data-testid="picker-mode-all" /> All rooms</label>
            <label><input type="radio" name={`af-${row.key}`} checked={mode === 'pick'} onChange={() => setMode('pick')} data-testid="picker-mode-pick" /> Pick rooms</label>
            {mode === 'pick' ? row.rooms.map((r) => (
              <label key={r.planId}>
                <input
                  type="checkbox"
                  checked={picked.includes(String(r.roomId))}
                  onChange={(event) => setPicked((prev) => (event.target.checked
                    ? [...prev, String(r.roomId)]
                    : prev.filter((id) => id !== String(r.roomId))))}
                  data-testid="picker-room-checkbox"
                  data-room-id={r.roomId}
                />
                {' '}{r.roomName}
              </label>
            )) : null}
          </div>
        ) : null}
        <div className="mpp-row mpp-gap">
          <label className="mpp-muted">
            Effective from{' '}
            <input type="date" value={from} onChange={(event) => setFrom(event.target.value)} data-testid="picker-effective-from" />
          </label>
          <button
            type="button"
            className="mpp-btn mpp-btn-primary mpp-btn-sm"
            disabled={chosen.length === 0 || !from}
            onClick={() => onAdd({ row, rooms: chosen, effectiveFrom: from })}
            data-testid="picker-add"
          >
            Add
          </button>
        </div>
        {multi && mode === 'pick' && chosen.length === 0 ? <div className="mpp-muted">Pick at least one room.</div> : null}
      </div>
    </div>
  );
};

const AddPlanPicker = ({
  pickerPlans, styles, rooms, alreadyAssignedPlanIds, defaultFrom, onAdd, onClose, newPlanHref, loading, error,
}) => {
  const [query, setQuery] = useState('');
  const [roomId, setRoomId] = useState('any');
  const [zonedOnly, setZonedOnly] = useState(false);

  const visible = useMemo(() => filterPickerPlans(
    pickerPlans
      .map((row) => ({ ...row, rooms: row.rooms.filter((r) => !alreadyAssignedPlanIds.has(String(r.planId))) }))
      .filter((row) => row.rooms.length > 0),
    { query, roomId, zonedOnly },
  ), [pickerPlans, alreadyAssignedPlanIds, query, roomId, zonedOnly]);

  return (
    <div className="mpp-picker" data-testid="price-plan-picker">
      <div className="mpp-row mpp-between">
        <input
          type="search"
          className="mpp-input"
          placeholder="Search plans"
          aria-label="Search plans"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          data-testid="picker-search"
        />
        <button type="button" className="mpp-icon-btn" aria-label="Close picker" onClick={onClose} data-testid="picker-close">&times;</button>
      </div>
      <div className="mpp-row mpp-gap" role="group" aria-label="Filters">
        <span className="mpp-muted">Room</span>
        <button type="button" className="mpp-chipbtn" aria-pressed={roomId === 'any'} onClick={() => setRoomId('any')}>Any</button>
        {rooms.map((room) => (
          <button
            type="button"
            key={room.id}
            className="mpp-chipbtn"
            aria-pressed={String(roomId) === String(room.id)}
            onClick={() => setRoomId(room.id)}
            data-testid="picker-room-filter"
          >
            {room.name}
          </button>
        ))}
        <button type="button" className="mpp-chipbtn" aria-pressed={zonedOnly} onClick={() => setZonedOnly((v) => !v)} data-testid="picker-zoned-only">Zoned only</button>
      </div>
      {loading ? <div className="mpp-muted">Loading plans&hellip;</div> : null}
      {error ? <div className="mpp-error" role="alert">{error}</div> : null}
      <div data-testid="picker-list">
        {visible.map((row) => (
          <PickerRow key={row.key} row={row} styles={styles} rooms={rooms} defaultFrom={defaultFrom} onAdd={onAdd} />
        ))}
        {!loading && visible.length === 0 ? <p className="mpp-muted" data-testid="picker-empty">No more plans match.</p> : null}
      </div>
      <a className="mpp-newplan" href={newPlanHref} data-testid="picker-new-plan">
        + New plan&hellip; <span className="mpp-muted">(opens Bookings Hub &rsaquo; Price plans, returns here)</span>
      </a>
    </div>
  );
};

export default AddPlanPicker;
